import {
  requestFingerprint,
  type ImportDisposition,
  type JsonObject,
} from "@medicorepay/shared";
import { productRevivalApi } from "./product-revival-client";

export type {
  ImportCommandResult,
  ImportDisposition,
  ImportErrorRecord,
  ImportFileMetadata,
  ImportJobRecord,
  ImportPreview,
  ImportReconciliation,
  ImportRowRecord,
  ImportStatus,
  MappingInference,
  MappingSuggestion,
  MergeProposal,
} from "@medicorepay/shared";

function commandKey(jobId: string, command: "commit" | "undo" | "reconcile") {
  const storageKey = `medicorepay:import:v2:${jobId}:${command}:key`;
  const existing = sessionStorage.getItem(storageKey);
  if (existing) return existing;
  const key = crypto.randomUUID();
  sessionStorage.setItem(storageKey, key);
  return key;
}

function profileCommandKey(jobId: string, request: unknown) {
  const storageKey = `medicorepay:import-profile:v3:${jobId}:${requestFingerprint(request)}:key`;
  const existing = sessionStorage.getItem(storageKey);
  if (existing) return existing;
  const key = crypto.randomUUID();
  sessionStorage.setItem(storageKey, key);
  return key;
}

function uploadCommandKey(file: File) {
  const storageKey = [
    "medicorepay:import-upload",
    "v2",
    file.name,
    file.size,
    file.lastModified,
    "key",
  ].join(":");
  const existing = sessionStorage.getItem(storageKey);
  if (existing) return existing;
  const key = `upload-${crypto.randomUUID()}`;
  sessionStorage.setItem(storageKey, key);
  return key;
}

function download(data: BlobPart, mimeType: string, filename: string) {
  const url = URL.createObjectURL(new Blob([data], { type: mimeType }));
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

export const productRevivalImportService = {
  upload: (file: File) =>
    productRevivalApi.uploadImportFile(file, undefined, {
      idempotencyKey: uploadCommandKey(file),
      clientApplication: "web",
    }),

  job: (jobId: string) => productRevivalApi.getImportJob(jobId),

  inferMapping: (jobId: string) => productRevivalApi.inferImportMapping(jobId),

  async saveAndApplyProfile(
    jobId: string,
    name: string,
    mapping: Record<string, string | null>,
    normalization?: JsonObject | null,
    sourceSystem?: string,
  ) {
    const request = {
      name,
      sourceSystem: sourceSystem ?? name,
      mapping,
      ...(normalization ? { normalization } : {}),
    };
    const profile = await productRevivalApi.saveImportProfile(request, {
      idempotencyKey: profileCommandKey(jobId, request),
      clientApplication: "web",
    });
    await productRevivalApi.applyImportProfile(
      jobId,
      { profileId: profile.id },
      {
        idempotencyKey: `apply-profile-${jobId}-${profile.id}`,
        clientApplication: "web",
      },
    );
    return profile;
  },

  parse: (jobId: string) =>
    productRevivalApi.parseImportJob(jobId, {
      idempotencyKey: crypto.randomUUID(),
      clientApplication: "web",
    }),

  revalidate: (jobId: string) =>
    productRevivalApi.revalidateImportJob(jobId, {
      idempotencyKey: crypto.randomUUID(),
      clientApplication: "web",
    }),

  preview: (jobId: string) => productRevivalApi.getImportPreview(jobId),

  decide: (
    jobId: string,
    decisions: Array<{
      rowId: string;
      disposition: ImportDisposition;
      entityId?: string;
      reason?: string;
    }>,
  ) =>
    productRevivalApi.applyImportDecisions(
      jobId,
      { decisions },
      {
        idempotencyKey: crypto.randomUUID(),
        clientApplication: "web",
      },
    ),

  correctRow: (jobId: string, rowId: string, values: Record<string, unknown>) =>
    productRevivalApi.correctImportRow(
      jobId,
      rowId,
      { values },
      {
        idempotencyKey: crypto.randomUUID(),
        clientApplication: "web",
      },
    ),

  reconcile: (jobId: string) =>
    productRevivalApi.reconcileImport(
      jobId,
      {},
      {
        idempotencyKey: commandKey(jobId, "reconcile"),
        clientApplication: "web",
      },
    ),

  getReconciliation: (jobId: string) =>
    productRevivalApi.getImportReconciliation(jobId),

  commit: (jobId: string) =>
    productRevivalApi.commitImport(jobId, {
      idempotencyKey: commandKey(jobId, "commit"),
      clientApplication: "web",
    }),

  undo: (jobId: string, reason: string) =>
    productRevivalApi.undoImport(
      jobId,
      { reason },
      {
        idempotencyKey: commandKey(jobId, "undo"),
        clientApplication: "web",
      },
    ),

  errors: (
    jobId: string,
    filters?: { code?: string; field?: string; severity?: string },
  ) => productRevivalApi.listImportErrors(jobId, filters),

  async downloadErrors(jobId: string) {
    const blob = await productRevivalApi.downloadImportErrors(jobId);
    download(blob, "text/csv", `import-${jobId}-errors.csv`);
  },

  async downloadReconciliation(jobId: string) {
    const blob = await productRevivalApi.downloadImportReconciliation(jobId);
    download(blob, "text/csv", `import-${jobId}-reconciliation.csv`);
  },
};
